import { Users, CalendarX } from '@phosphor-icons/react';
import type { RoomOfferDTO } from '@pms/shared';
import { formatPrice } from '../../lib/format';

interface RoomListProps {
  rooms: RoomOfferDTO[];
  guests: number;
  hasDates: boolean;
  availableCount: number;
  selectedUnitId: string | null;
  onSelect: (unitId: string) => void;
  onChangeDates: () => void;
}

export function RoomList({
  rooms, guests, hasDates, availableCount, selectedUnitId, onSelect, onChangeDates,
}: RoomListProps) {
  if (rooms.length === 0) {
    return (
      <div className="rounded-2xl border border-sand bg-white p-8 text-center">
        <p className="font-semibold text-ink">No rooms listed for this hotel yet.</p>
      </div>
    );
  }

  return (
    <section aria-labelledby="rooms-h" className="border-t border-sand pt-8">
      <h2 id="rooms-h" className="font-serif text-2xl font-bold text-ink">Choose your room</h2>

      {hasDates && availableCount === 0 && (
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-clay-light bg-clay-light/40 px-4 py-3">
          <p className="flex items-center gap-2 text-sm font-medium text-clay-dark">
            <CalendarX className="h-4 w-4" /> Not available for these dates.
          </p>
          <button type="button" onClick={onChangeDates} className="text-sm font-semibold text-clay hover:underline">
            Change dates
          </button>
        </div>
      )}

      <ul role="radiogroup" aria-label="Room options" className="mt-5 space-y-3">
        {rooms.map((room) => {
          const selectable = room.available && room.fits && room.meetsMinStay;
          const selected = room.unitId === selectedUnitId;
          const reason = !room.fits
            ? `Fits ${room.capacity} — you searched ${guests}`
            : !room.meetsMinStay
              ? `Minimum ${room.minimumStay} nights`
              : !room.available
                ? 'Not available for these dates'
                : null;

          return (
            <li
              key={room.unitId}
              className={`rounded-[1.25rem] border bg-white p-5 transition ${
                selected ? 'border-clay ring-2 ring-clay' : 'border-sand'
              } ${selectable ? '' : 'opacity-70'}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="font-serif text-lg font-semibold text-ink">{room.name}</h3>
                  <p className="mt-1 flex items-center gap-1.5 text-sm capitalize text-ink-muted">
                    {room.type} · <Users className="h-3.5 w-3.5" /> Fits {room.capacity}
                  </p>
                  {reason && <p className="mt-1.5 text-xs font-medium text-clay-dark">{reason}</p>}
                </div>

                <div className="text-right">
                  {room.perStayTotal != null ? (
                    <>
                      <p className="font-serif text-xl font-bold text-ink">
                        {formatPrice(room.perStayTotal, room.currency)}
                      </p>
                      <p className="text-xs text-ink-muted">
                        for {room.nights} night{room.nights === 1 ? '' : 's'}
                      </p>
                    </>
                  ) : (
                    <p className="text-ink">
                      <span className="font-serif text-xl font-bold">{formatPrice(room.nightlyFrom, room.currency)}</span>
                      <span className="text-sm text-ink-muted"> / night</span>
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  disabled={!selectable}
                  onClick={() => onSelect(room.unitId)}
                  className={`rounded-full px-6 py-2 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clay focus-visible:ring-offset-2 ${
                    selected
                      ? 'bg-clay text-white'
                      : selectable
                        ? 'border border-clay text-clay hover:bg-clay hover:text-white'
                        : 'cursor-not-allowed border border-sand text-ink-muted'
                  }`}
                >
                  {selected ? 'Selected' : 'Select'}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
